import { useCallback, useEffect, useMemo, useState } from "react";

import { ChipList, StatusBadge } from "@/app/library/components/molecules";
import { SuperAdminTenantsStore } from "@/app/tenants/data_sources/store";
import type { APISuperAdminTenantResponseType } from "@/core/data_sources/models/APISuperAdminTenant";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  AdminTableToolbar,
  DataTable,
  PageHeader,
} from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";

import {
  tenantPlans,
  tenantStatuses,
  tenantVerticals,
  type Tenant,
  type TenantPlan,
  type TenantStatus,
  type TenantVertical,
} from "../../utils";
import { TenantDetailDrawer } from "./components/TenantDetailDrawer";
import { TenantSetupDialog } from "./components/TenantSetupDialog";
import { TenantSuccessDialog } from "./components/TenantSuccessDialog";

type PlanFilter = TenantPlan | "all";
type StatusFilter = TenantStatus | "all";
type VerticalFilter = TenantVertical | "all";

export function TenantsPage() {
  const { actions: appActions } = useStore(AppStore.instance);
  const {
    actions: tenantActions,
    state: tenantsState,
    hooks,
  } = useStore(SuperAdminTenantsStore.instance);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<PlanFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [verticalFilter, setVerticalFilter] = useState<VerticalFilter>("all");
  const { setQuery: setTenantsQuery } = hooks.useTenantsStoreService({
    page: 1,
    page_size: 100,
    search: "",
  });
  useEffect(() => {
    setTenantsQuery((prev) => ({
      ...prev,
      search: search.trim(),
    }));
  }, [search, setTenantsQuery]);
  const apiTenants = useMemo(
    () => tenantsState?.list.map((tenant) => tenant.getData()) ?? [],
    [tenantsState?.list],
  );
  const tenantRows = useMemo(() => apiTenants.map(toTenantRow), [apiTenants]);
  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);
  const closeDrawer = useCallback(() => {
    appActions.rightDrawer("close");
  }, [appActions]);
  const openTenantDrawer = useCallback(
    (tenant: Tenant) => {
      appActions.rightDrawer(
        "open",
        620,
        <TenantDetailDrawer onClose={closeDrawer} tenant={tenant} />,
      );
    },
    [appActions, closeDrawer],
  );
  const openTenantSuccessDialog = useCallback(
    (tenant: Tenant) => {
      appActions.dialog(
        "open",
        <DialogTemplate className="max-w-none">
          <TenantSuccessDialog
            onClose={closeDialog}
            onOpenTenant={() => {
              closeDialog();
              openTenantDrawer(tenant);
            }}
            tenant={tenant}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, openTenantDrawer],
  );
  const openCreateTenantDialog = useCallback(() => {
    appActions.dialog(
      "open",
      <DialogTemplate className="max-w-none">
        <TenantSetupDialog
          onClose={closeDialog}
          onCreate={(tenant) => {
            tenantActions.setTenants([toAPITenant(tenant), ...apiTenants]);
            openTenantSuccessDialog(tenant);
          }}
        />
      </DialogTemplate>,
    );
  }, [
    apiTenants,
    appActions,
    closeDialog,
    openTenantSuccessDialog,
    tenantActions,
  ]);
  const filteredTenants = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return tenantRows.filter((tenant) => {
      const haystack = [
        tenant.name,
        tenant.owner,
        tenant.country,
        tenant.plan,
        tenant.vertical,
        tenant.status,
        tenant.billing,
      ]
        .join(" ")
        .toLowerCase();

      return (
        (!normalizedSearch || haystack.includes(normalizedSearch)) &&
        (planFilter === "all" || tenant.plan === planFilter) &&
        (statusFilter === "all" || tenant.status === statusFilter) &&
        (verticalFilter === "all" || tenant.vertical === verticalFilter)
      );
    });
  }, [planFilter, search, statusFilter, tenantRows, verticalFilter]);
  const columns = useMemo(
    () => [
      {
        header: "Tenant",
        key: "tenant",
        render: (tenant: Tenant) => (
          <button
            className="text-left"
            onClick={() => openTenantDrawer(tenant)}
            type="button"
          >
            <div className="font-extrabold text-ink">{tenant.name}</div>
            <div className="text-xs text-ink-muted">
              {tenant.owner} · {tenant.country}
            </div>
          </button>
        ),
      },
      {
        header: "Plan",
        key: "plan",
        render: (tenant: Tenant) => tenant.plan,
      },
      {
        header: "Vertical",
        key: "vertical",
        render: (tenant: Tenant) => tenant.vertical,
      },
      {
        header: "Status",
        key: "status",
        render: (tenant: Tenant) => <StatusBadge status={tenant.status} />,
      },
      {
        header: "Packs",
        key: "packs",
        render: (tenant: Tenant) => (
          <ChipList values={getTenantPacks(tenant)} />
        ),
      },
      {
        header: "Users",
        key: "users",
        render: (tenant: Tenant) => tenant.users,
      },
      {
        header: "Billing",
        key: "billing",
        render: (tenant: Tenant) => tenant.billing,
      },
      {
        className: "w-20",
        header: "",
        key: "actions",
        render: (tenant: Tenant) => (
          <Button onClick={() => openTenantDrawer(tenant)} size="sm">
            Open
          </Button>
        ),
      },
    ],
    [openTenantDrawer],
  );

  return (
    <>
      <PageHeader
        actions={
          <>
            <Button onClick={openCreateTenantDialog} variant="primary">
              Create Tenant
            </Button>
            <Button>Export CSV</Button>
          </>
        }
        breadcrumb={["Super Admin", "Tenants"]}
        subtitle="Create and manage tenants. Tenant Setup installs allowed Template Packs."
        title="Tenants"
      />
      <DataTable
        columns={columns}
        header={
          <AdminTableToolbar>
            <input
              className={`${adminInputClasses} w-80 max-w-full`}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search tenants..."
              value={search}
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setPlanFilter(event.target.value as PlanFilter)
                }
                value={planFilter}
              >
                <option value="all">All plans</option>
                {tenantPlans.map((plan) => (
                  <option key={plan}>{plan}</option>
                ))}
              </select>
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setStatusFilter(event.target.value as StatusFilter)
                }
                value={statusFilter}
              >
                <option value="all">All status</option>
                {tenantStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setVerticalFilter(event.target.value as VerticalFilter)
                }
                value={verticalFilter}
              >
                <option value="all">All verticals</option>
                {tenantVerticals.map((vertical) => (
                  <option key={vertical}>{vertical}</option>
                ))}
              </select>
            </div>
          </AdminTableToolbar>
        }
        rowKey={(tenant) => tenant.id}
        rows={filteredTenants}
      />
    </>
  );
}

function getTenantPacks(tenant: Tenant) {
  if (tenant.vertical === "HVAC") {
    return ["General Service Pack", "HVAC Starter Pack"];
  }
  if (tenant.vertical === "Facility Mgmt") {
    return ["General Service Pack", "Facility Starter Pack"];
  }
  if (tenant.vertical === "Telco") {
    return ["General Service Pack"];
  }
  return ["General Service Pack"];
}

function toTenantRow(tenant: APISuperAdminTenantResponseType): Tenant {
  const id = String(tenant.id ?? tenant.domain ?? tenant.name ?? "");
  return {
    billing: "Manual / Invoice",
    code: tenant.domain ?? id,
    country: "-",
    countryCode: "-",
    createdAt: "-",
    id,
    lastLogin: "-",
    name: tenant.name ?? tenant.domain ?? "Tenant",
    owner: tenant.domain ?? "-",
    ownerName: "-",
    plan: "Enterprise",
    planVersion: 0,
    product: "Enterprise",
    status: tenant.registration_completed ? "active" : "trial",
    users: 0,
    vertical: "Generic",
  };
}

function toAPITenant(tenant: Tenant): APISuperAdminTenantResponseType {
  const parsedId = Number(tenant.id);
  return {
    id: Number.isFinite(parsedId) ? parsedId : Date.now(),
    name: tenant.name,
    domain: tenant.code,
    registration_completed: tenant.status === "active",
  };
}
